import { useState } from "react";
import { easeOut, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useAppContext } from "../common/utils/CustomHooks/useAppContext";
import { City } from "../config/googleForm.config";

const CitySelectionPage = () => {
  const navigate = useNavigate();
  const { setSelectedCity, appState } = useAppContext();
  const { enabledCities } = appState;

  const [search, setSearch] = useState("");

  const handleCitySelect = (city: City) => {
    setSelectedCity(city);
    navigate("/form");
  };

  const allCities: { name: City; tagline: string }[] = [
    {
      name: "Pune",
      tagline: "Cultural Capital of Maharashtra",
    },
    {
      name: "Mumbai",
      tagline: "City of Dreams",
    },
    {
      name: "Nagpur",
      tagline: "Orange City",
    },
    {
      name: "Ahmedabad",
      tagline: "Manchester of India",
    },
    {
      name: "Surat",
      tagline: "Diamond City",
    },
    {
      name: "Nashik",
      tagline: "Wine Capital of India",
    },
    {
      name: "Kolhapur",
      tagline: "Royal Heritage City",
    },
    {
      name: "Sambhajinagar",
      tagline: "Gateway to Ajanta & Ellora",
    },
    {
      name: "Bengaluru",
      tagline: "Silicon Valley of India",
    },
    {
      name: "Hyderabad",
      tagline: "City of Pearls",
    },
    {
      name: "Lucknow",
      tagline: "City of Nawabs",
    },
    {
      name: "Goa",
      tagline: "Beaches & Coastal Escape",
    },
    {
      name: "Delhi",
      tagline: "National Capital Region",
    },
  ];

  const availableCities = allCities
    .filter((city) => enabledCities.includes(city.name))
    .filter((city) => city.name.toLowerCase().includes(search.toLowerCase()));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: easeOut },
    },
  };

  const popularCities = [
    "Mumbai",
    "Pune",
    "Bengaluru",
    "Hyderabad",
    "Delhi",
  ] as City[];

  return (
    <main className="min-h-screen pt-24 bg-gradient-to-b from-background via-background to-primary/5 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          className="mb-12 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link
            to="/"
            className="inline-block text-primary hover:text-primary/80 mb-6 font-semibold transition-colors"
          >
            ← Back to Home
          </Link>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light italic text-primary mb-4">
            Choose Your City
          </h1>

          <p className="text-lg sm:text-xl text-foreground/60 max-w-2xl mx-auto">
            Select your city to begin your blind date journey
          </p>
        </motion.div>

        {/* Search */}
        <motion.div
          className="max-w-xl mx-auto mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <input
            type="text"
            placeholder="Search your city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-2xl border border-border bg-card px-5 py-4 text-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </motion.div>

        {/* Popular Cities */}
        <motion.div
          className="mb-10 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-sm uppercase tracking-wider text-foreground/50 mb-4">
            Popular Cities
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            {popularCities
              .filter((city) => enabledCities.includes(city))
              .map((city) => (
                <button
                  key={city}
                  onClick={() => handleCitySelect(city)}
                  className="px-5 py-2 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all duration-300"
                >
                  {city}
                </button>
              ))}
          </div>
        </motion.div>

        {/* City Grid */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {availableCities.map((city) => (
            <motion.button
              key={city.name}
              variants={itemVariants}
              whileHover={{
                scale: 1.03,
                y: -4,
              }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleCitySelect(city.name)}
              className="bg-card rounded-2xl border border-border hover:border-primary p-5 text-left shadow-sm hover:shadow-lg transition-all duration-300"
            >
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {city.name}
              </h3>

              <p className="text-sm text-foreground/60 mb-4">{city.tagline}</p>
            </motion.button>
          ))}
        </motion.div>

        {/* Empty State */}
        {availableCities.length === 0 && (
          <div className="text-center py-16">
            <p className="text-lg text-foreground/60">
              No cities found matching "{search}"
            </p>
          </div>
        )}

        {/* Footer Info */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <p className="text-foreground/50 text-sm">
            Your city selection helps us match you with people in your area
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export { CitySelectionPage };
